<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import type { HomePageConfig } from '../configs/types'
import ModuleBannerSlider from './modules/ModuleBannerSlider.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.vue'
import ModuleFeed from './modules/ModuleFeed.vue'
import ModuleTagsWithBg from './modules/ModuleTagsWithBg.vue'
import ModuleRankingBig from './modules/ModuleRankingBig.vue'
import ModuleBookSwiper from './modules/ModuleBookSwiper.vue'
import { COLUMN_TYPE } from '@/constants'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  homeBgUrl: any
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

// 定义 emit 以保持与 default 组件的类型兼容性
defineEmits<{
  (e: 'update:input', value: string): void
}>()

const router = useRouter()

const localHomeBgUrl = ref(props.homeBgUrl)
watch(() => props.homeBgUrl, (newVal) => {
  localHomeBgUrl.value = newVal
}, { immediate: true })

// 更新背景图
function handleUpdateBgUrl(url: string) {
  localHomeBgUrl.value = url
}

// 定义样式变量供 CSS v-bind 使用
const homeBgUrlComputed = computed(() => `url(${localHomeBgUrl.value})`)
const tabActiveColor = computed(() => props.config.theme.tabActiveColor)
const stickyHeaderBg = computed(() => props.config.theme.stickyHeaderBg)

// 获取 BannerSlider 模块（SLIDE_BOOK 类型）
const bannerModule = computed(() => {
  return props.modules.find(col => col.columnType === COLUMN_TYPE.SLIDE_BOOK)
})

// 获取其他模块（非 SLIDE_BOOK 类型）
const otherModules = computed(() => {
  return props.modules.filter(col => col.columnType !== COLUMN_TYPE.SLIDE_BOOK)
})

// 是否有 Banner 模块
const hasBanner = computed(() => !!bannerModule.value)

// 滚动状态
const scrollTop = ref(0)
const scrollContainer = ref<HTMLElement | null>(null)

// 是否显示 sticky-header 背景
// 有 Banner 时：滚动超过 10px 才显示背景
// 无 Banner 时：始终显示背景
const showHeaderBg = computed(() => {
  if (!hasBanner.value)
    return true
  return scrollTop.value > 10
})

// 监听滚动
function handleScroll(e: Event) {
  const target = e.target as HTMLElement
  scrollTop.value = target.scrollTop
}

// 设置滚动容器引用
function setScrollRef(el: HTMLElement | null) {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)

  scrollContainer.value = el
  if (el)
    el.addEventListener('scroll', handleScroll, { passive: true })

  props.setInfiniteRef(el)
}

onUnmounted(() => {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div :ref="setScrollRef" class="h-100dvh overflow-y-scroll">
    <!-- 固定头部：品牌 Header + Tab + 搜索图标 -->
    <!-- light-mode: 无 Banner 或滚动时显示背景，使用深色文字 -->
    <div class="sticky-header pt-0px" :class="{ 'show-bg': showHeaderBg, 'light-mode': !hasBanner || showHeaderBg }">
      <!-- 品牌 Header：Logo + 金币入口 -->
      <div class="brand-row flex items-center justify-between px-15px">
        <img
          src="/images/vowpath/coins/brand-logo.webp"
          class="brand-logo"
          alt="Vowpath"
        >
        <CoinEntry />
      </div>
      <!-- Tab + 搜索图标 -->
      <div class="px-15px">
        <div class="flex items-center justify-between">
          <!-- Tab 列表（可横向滚动） -->
          <div class="flex items-center space-x-24px overflow-x-auto flex-1 tab-scroll">
            <div v-for="item in pageList" :key="item.id">
              <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
                <span>{{ item.showName }}</span>
                <i v-if="item.id === pageInfo.id" class="tab-underline" />
              </div>
            </div>
          </div>
          <!-- 搜索图标 -->
          <div class="search-icon-btn ml-12px shrink-0 cursor-pointer flex items-center justify-center" @click="onSearch">
            <img
              v-if="hasBanner && !showHeaderBg"
              src="/images/vowpath/icon-search-white.png"
              width="20px"
              height="20px"
            >
            <img
              v-else
              src="/images/vowpath/search-icon.png"
              width="20px"
              height="20px"
            >
          </div>
        </div>
      </div>
    </div>

    <div class="home-bg pb-20">
      <!-- Modules -->
      <div v-if="skeleton" class="h-100dvh">
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
        <p class="mt-4 text-center font-bold">
          {{ config.texts.loading }}
        </p>
      </div>
      <div v-else class="px-0">
        <!-- Banner 模块 + 背景：始终在最上方，一起滚动 -->
        <div v-if="bannerModule" class="banner-wrapper">
          <div class="home-header-bg" />
          <ModuleBannerSlider
            class="relative z-1"
            :module="bannerModule"
            :config="config"
            :goto-book="gotoBook"
            :goto-collection="gotoCollection"
            card-class="p-0"
            @update:bg-url="handleUpdateBgUrl"
          />
        </div>

        <!-- 其他模块 -->
        <div v-for="(col, index) in otherModules" :key="col.id" :class="{ 'mt-10px': index > 0 || bannerModule }">
          <!-- 2行3列模块 -->
          <template v-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_3">
            <div class="mx-10px pt-14px bg-white rounded-8px">
              <ModuleTagsWithBg
                card-class="p-0"
                :module="col"
                :config="config"
                :current-tag-id="currentTagId"
                :goto-book="gotoBook"
                :goto-tags="gotoTags"
                :get-current-tag-resources="getCurrentTagResources"
                :get-current-show-tags="getCurrentShowTags"
                :handle-tag-click="handleTagClick"
              />
            </div>
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
            <div class="mx-10px py-14px bg-white rounded-8px">
              <ModuleFeatured1Plus4
                card-class="p-0 px-15px"
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
              />
            </div>
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
            <div class="mx-10px py-14px bg-white rounded-8px">
              <ModuleGrid2x4
                card-class="p-0 px-15px"
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
              />
            </div>
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
            <div class="mx-10px py-14px bg-white rounded-8px">
              <ModuleGrid1x4
                card-class="p-0 px-15px"
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
              />
            </div>
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
            <div class="mt-12px mx-10px py-14px bg-white rounded-8px">
              <ModuleRankingBig :module="col" :config="config" card-class="p-0 px-10px" class="w-full " />
            </div>
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
            <div class="mx-10px py-14px bg-white rounded-8px">
              <ModuleBookSwiper :module="col" :config="config" card-class="p-0 px-10px" class="w-full" />
            </div>
          </template>
        </div>
      </div>

      <!-- Feed -->
      <ModuleFeed
        v-if="feeds.length"
        card-class="p-0 px-15px mt-6"
        :feeds="feeds"
        :feed-show-name="feedShowName"
        :feed-id="feedId"
        :feeds-column-type="feedsColumnType"
        :feed-fetching="feedFetching"
        :feed-ending="feedEnding"
        :config="config"
        :goto-book="gotoBook"
      />
    </div>

    <BasicTabbar />
  </div>
</template>

<style lang="scss" scoped>
/* 固定头部：搜索 + Tab */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;

  /* 背景层用伪元素实现 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    // background: v-bind(stickyHeaderBg);
    background: #F6F7F9;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  /* 显示背景时 */
  &.show-bg::before {
    opacity: 1;
  }
}

/* Banner 容器：包含背景和 Banner 模块 */
.banner-wrapper {
  position: relative;
  margin-top: -90px; /* 向上偏移，让背景延伸到 sticky-header 下方 */
  padding-top: 110px; /* 补回内容的上边距 */
}

/* 顶部背景层：相对于 banner-wrapper 定位 */
.home-header-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 256px;
  z-index: 0;
  background: rgba(0, 0, 0, 0.6); /* 深色底色 */
  /* 底部弧度：向下凸出 */
  border-radius: 0 0 50% 50% / 0 0 30px 30px;

  /* 背景图片 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: v-bind(homeBgUrlComputed);
    background-repeat: no-repeat;
    background-position: top center;
    background-size: cover;
    -webkit-mask-image: linear-gradient(rgba(0,0,0,0.6), transparent);
    mask-image: linear-gradient(rgba(0,0,0,0.6), transparent);
    filter: blur(5px);
    transition: background-image 0.5s;
    border-radius: inherit;
  }

  /* 深色蒙层 */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    border-radius: inherit;
  }
}

.home-bg {
  position: relative;
  z-index: 1;
}

/* 品牌 Header 行 */
.brand-row {
  height: 44px;
}

.brand-logo {
  height: 25px;
  width: auto;
}

/* Tab 列表禁止滚动条显示 */
.tab-scroll {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

/* 有 Banner 时的 Tab 样式 */
.home-tab {
  cursor: pointer;
  padding: 10px 0;

  span {
    font-family: Inter, Inter;
    font-weight: 500;
    font-size: 14px;
    color: #FFFFFF;
    opacity: 0.7;
    line-height: 16px;
    white-space: nowrap;
  }
}

/* light-mode 时的 Tab 样式（无 Banner 或滚动时） */
.sticky-header.light-mode .home-tab span {
  color: #32353A;
  opacity: 1;
}

.home-tab.active {
  span {
    position: relative;
    font-size: 20px;
    background-clip: text;
    opacity: 1;
    font-weight: bold;
    z-index: 10;
  }

  .tab-underline {
    display: block;
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 18px;
    height: 6px;
    border-radius: 2px;
    width: 100%; /* 43px */
    background-image: linear-gradient(270deg, #FF6BD2 0%, #A162FF 100%), linear-gradient(270deg, #F6F7F9 0%, #9875FB 95%);
    border-radius: 3px;
  }
}
</style>
